"use client";

import { useCookieConsent, getDefaultConsent } from "@/contexts/CookieConsentContext";
import type { CookieConsent } from "@/lib/cookie-consent";
import { fsElement } from "@/lib/fs";

type CookiePreferencesProps = {
  /** When true, show a compact layout (e.g. inside modal). When false, show as full section (e.g. Account tab). */
  compact?: boolean;
};

export function CookiePreferences({ compact = false }: CookiePreferencesProps) {
  const { consent, setConsent, clearConsent, ready } = useCookieConsent();
  const current = consent ?? getDefaultConsent();

  const handleAnalyticsChange = (checked: boolean) => {
    setConsent({ ...current, analytics: checked });
  };

  const handleMarketingChange = (checked: boolean) => {
    setConsent({ ...current, marketing: checked });
  };

  const handleAcceptAll = () => {
    setConsent({ necessary: true, analytics: true, marketing: true });
  };

  const handleRejectNonEssential = () => {
    setConsent(getDefaultConsent());
  };

  if (!ready) return null;

  const content = (
    <>
      <p className="text-sm text-muted-foreground mb-4">
        You can choose which optional cookies we use. Necessary cookies are required for the site to work (e.g. login, session).
      </p>

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 p-3">
          <div>
            <p className="font-medium text-foreground">Strictly necessary</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Required for login, session and security. Cannot be disabled.
            </p>
          </div>
          <span className="shrink-0 text-sm text-muted-foreground">Always on</span>
        </div>

        <label className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-3 cursor-pointer hover:bg-muted/30 transition-colors">
          <div>
            <p className="font-medium text-foreground">Analytics</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Helps us understand how the site is used (e.g. page views). We do not use analytics scripts yet; this setting will apply when we do.
            </p>
          </div>
          <input
            type="checkbox"
            checked={current.analytics}
            onChange={(e) => handleAnalyticsChange(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border"
          />
        </label>

        <label className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-3 cursor-pointer hover:bg-muted/30 transition-colors">
          <div>
            <p className="font-medium text-foreground">Marketing</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Used for marketing or advertising cookies if we use them in future. We do not use marketing cookies at the moment.
            </p>
          </div>
          <input
            type="checkbox"
            checked={current.marketing}
            onChange={(e) => handleMarketingChange(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border"
          />
        </label>
      </div>

      {!compact && (
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="button"
            onClick={handleAcceptAll}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            {...fsElement("Accept All Cookies Button", { id: "prefs-accept-all", section: "cookie-preferences" })}
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={handleRejectNonEssential}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
            {...fsElement("Reject Non-Essential Cookies Button", { id: "prefs-reject", section: "cookie-preferences" })}
          >
            Reject non-essential
          </button>
          {consent !== null && (
            <button
              type="button"
              onClick={clearConsent}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
              {...fsElement("Reset Cookie Choices Button", { id: "prefs-reset", section: "cookie-preferences" })}
            >
              Reset my choices
            </button>
          )}
        </div>
      )}
    </>
  );

  if (compact) {
    return <div className="p-1">{content}</div>;
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-base font-semibold text-foreground mb-1">Cookies &amp; Tracking</h2>
      {content}
    </div>
  );
}
